/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import IObservable from '../Utility/Observable/IObservable';
import Observable from '../Utility/Observable/Observable';
import TypeStrings from '../Utility/TypeStrings';

/** Display currency types. */
export enum CurrencyType
{
    UKNOWN = 0,
    MC = 1,
    CREDITS = 2,
    CASH = 3
}

/** Currency class for converting values for display purposes. */
export default class Currency
{
    /** Active denomination for conversions. */
    protected activeDenomObservable: IObservable<number> = null;
    public get ActiveDenomObservable(): IObservable<number> { return this.activeDenomObservable; }

    /** Millicent value, only value stored. */
    protected mcValue: Observable<number> = new Observable<number>(0);
    /** Public accessor to millicent value. */
    public get McValue(): Observable<number> { return this.mcValue; }

    public HasValue(): boolean { return this.mcValue.Value !== 0; }

    /**
     * Creates new currency which will convert based on denom value.
     * @param activeDenomObservable Observable denomination value to use for conversions.
     * @param startValue Starting value.
     * @param startValueType Given type of start value.
     */
    constructor(
        activeDenomObservable: IObservable<number> | Currency | number,
        startValue: number,
        startValueType: CurrencyType = CurrencyType.MC)
    {
        if (activeDenomObservable instanceof Currency)
        {
            this.activeDenomObservable = (activeDenomObservable as Currency).activeDenomObservable;
        }
        else if (typeof(activeDenomObservable) === TypeStrings.number)
        {
            this.activeDenomObservable = new Observable<number>(activeDenomObservable as number);
        }
        else
        {
            this.activeDenomObservable = activeDenomObservable as IObservable<number>;
        }

        this.SetValue(startValue, startValueType);
    }

    // #region Currency Functions

    public Add(currency: Currency): Currency
    {
        this.mcValue.Value += currency.GetValue();
        return this;
    }

    public Subtract(currency: Currency): Currency
    {
        this.mcValue.Value -= currency.GetValue();
        return this;
    }

    public Set(currency: Currency): Currency
    {
        this.mcValue.Value = currency.GetValue();
        return this;
    }

    public Difference(otherCurrency: Currency): Currency
    {
        let diff: Currency = new Currency(otherCurrency.activeDenomObservable,
            this.mcValue.Value - otherCurrency.GetValue());

        return diff;
    }

    /** Rests the millicent value to 0. */
    public Clear(): Currency
    {
        this.mcValue.Value = 0;
        return this;
    }

    public Clone(observeDenom = true): Currency
    {
        return new Currency(
            observeDenom ? this.activeDenomObservable : this.activeDenomObservable.Value,
            this.mcValue.Value
        );
    }

    // #endregion

    // #region Value Functions

    /**
     * Add numberic value to currency.
     * @param value Number value to add to currency.
     * @param type Type of value being added.
     * @returns This [[Currency]]
     */
    public AddValue(value: number, type: CurrencyType = CurrencyType.MC): Currency
    {
        this.mcValue.Value += Currency.ConvertToMC(value, type, this.activeDenomObservable.Value);
        return this;
    }

    /**
     * Add numberic value to currency.
     * @param value Number value to subtract from currency.
     * @param type Type of value being subtracted.
     * @returns This [[Currency]]
     */
    public SubtractValue(value: number, type: CurrencyType): Currency
    {
        this.mcValue.Value -= Currency.ConvertToMC(value, type, this.activeDenomObservable.Value);
        return this;
    }

    /**
     * Set currency to given number.
     * @param value Number to set to.
     * @param type Type of value being set to.
     * @returns This [[Currency]]
     */
    public SetValue(value: number, type: CurrencyType = CurrencyType.MC): Currency
    {
        // no fractional millicent
        this.mcValue.Value = Math.floor(Currency.ConvertToMC(value, type, this.activeDenomObservable.Value));
        return this;
    }

    /**
     * Get converted value.
     * @param type Type of return currency.
     * @returns Converted value.
     */
    public GetValue(type: CurrencyType = CurrencyType.MC): number
    {
        return Currency.ConvertFromMC(this.mcValue.Value, type, this.activeDenomObservable.Value);
    }

    // #endregion

    // #region Converter Helpers

    /**
     * Convert value to millicents.
     * @param value Number value to be converted.
     * @param type Type to convert to.
     * @param denom Denomination used for credit conversion.
     */
    public static ConvertFromMC(value: number, type: CurrencyType, denom: number): number
    {
        switch (type)
        {
            case CurrencyType.MC:
                return value;
            case CurrencyType.CREDITS:
                return Currency.ConvertMCToCredits(value, denom);
            case CurrencyType.CASH:
                return Currency.ConvertMCToCash(value);
            case CurrencyType.UKNOWN:
            default:
                CLog.error('Uknown currency type.', null, LogTypes.Localization);
                return value;
        }
    }

    /**
     * Converts value from millecents.
     * @param value Number value to be converted.
     * @param type Type of number that is being converted.
     * @param denom Denomination used for credit conversion.
     */
    public static ConvertToMC(value: number, type: CurrencyType, denom: number): number
    {
        switch (type)
        {
            case CurrencyType.MC:
                return value;
            case CurrencyType.CREDITS:
                return Currency.ConvertCreditsToMC(value, denom);
            case CurrencyType.CASH:
                return Currency.ConvertCashToMC(value);
            case CurrencyType.UKNOWN:
            default:
                CLog.error('Uknown currency type.', null, LogTypes.Localization);
                return Math.floor(value);
        }
    }

    /**
     * Convenience method for converting millicents to credits.
     * @param mc The amount of millicents to convert.
     * @returns The value of the millicents in credits.
     */
    public static ConvertMCToCredits(mc: number, activeDenom: number): number
    {
        return Math.floor(mc / activeDenom);
    }

    /**
     * Convenience method for converting millicents to currency.
     * @param mc The amount in millicents to convert.
     * @returns The value of the millicents in currency.
     */
    public static ConvertMCToCash(mc: number): number
    {
        let cents: number = mc / 1000.0;
        let dollars: number = cents / 100.0;
        return dollars;
    }

    /**
     * Convenience method for converting credits to millicents.
     * @param credits The amount of credits to convert.
     * @returns The value of the credits in millicents.
     */
    public static ConvertCreditsToMC(credits: number, activeDenom: number): number
    {
        return Math.floor(credits * activeDenom);
    }

    /**
     * Convenience method for converting cash to millicents.
     * @param cash The amount of cash to convert.
     * @returns The value of the cash in millicents.
     */
    public static ConvertCashToMC(cash: number): number
    {
        let cents: number = cash * 100.0;
        let mc: number = cents * 1000.0;
        return Math.floor(mc);
    }

    // #endregion
}
